import { MergeCategoryEnum } from 'libs/end-to-end/src/merge';
import { z } from 'zod';
import { AIInterviewerDebugMode } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { QuestionRubric } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-rubric';
import { BaseIntegration } from 'libs/end-to-end/src/types/databases/integrations/base-integration';
import { ResumeQualityScoreConfig } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { ResumeParsingCacheBusterDefaultVersion } from 'libs/end-to-end/src/types/databases/integrations/resume-parsing-cache-buster';

export const MergeIntegrationModelListItem = z.object({
  model: z.string(),
  filename: z.string(),
  args: z.record(z.string(), z.unknown()).optional(),
});

export type MergeIntegrationModelListItem = z.infer<
  typeof MergeIntegrationModelListItem
>;

export const MergeIntegrationPassthroughRequestCallTypeValue = z.enum([
  'single',
  'groupByEntity',
]);

export type MergeIntegrationPassthroughRequestCallTypeValue = z.infer<
  typeof MergeIntegrationPassthroughRequestCallTypeValue
>;

export const MergeIntegrationPassthroughRequestCallTypeValues = Object.values(
  MergeIntegrationPassthroughRequestCallTypeValue.Values,
);

export const MergeIntegrationPassthroughRequestSingleCallType = z.object({
  callType: z.intersection(
    MergeIntegrationPassthroughRequestCallTypeValue,
    z.literal('single'),
  ),
});

export type MergeIntegrationPassthroughRequestSingleCallType = z.infer<
  typeof MergeIntegrationPassthroughRequestSingleCallType
>;

export const MergeIntegrationPassthroughRequestGroupByEntityCallType = z.object(
  {
    callType: z.intersection(
      MergeIntegrationPassthroughRequestCallTypeValue,
      z.literal('groupByEntity'),
    ),
    entity: z.string(),
  },
);

export type MergeIntegrationPassthroughRequestGroupByEntityCallType = z.infer<
  typeof MergeIntegrationPassthroughRequestGroupByEntityCallType
>;

export const MergeIntegrationPassthroughRequestCallType = z.union([
  MergeIntegrationPassthroughRequestSingleCallType,
  MergeIntegrationPassthroughRequestGroupByEntityCallType,
]);

export type MergeIntegrationPassthroughRequestCallType = z.infer<
  typeof MergeIntegrationPassthroughRequestCallType
>;

export const MergeIntegrationPassthroughRequestBase = z.object({
  acceptErrors: z.boolean(),
  filename: z.string(),
  url: z.string(),
});

export type MergeIntegrationPassthroughRequestBase = z.infer<
  typeof MergeIntegrationPassthroughRequestBase
>;

export const MergeIntegrationPassthroughRequest =
  MergeIntegrationPassthroughRequestBase.extend({
    callType: MergeIntegrationPassthroughRequestCallType,
  });

export type MergeIntegrationPassthroughRequest = z.infer<
  typeof MergeIntegrationPassthroughRequest
>;

export const MergeIntegration = BaseIntegration.extend({
  integrationType: z.literal('Merge'),
  linkToken: z.string(),
  magicLinkUrl: z.string(),
  accountToken: z.string(),
  category: MergeCategoryEnum,
  aiInterviewerEnabled: z.boolean().optional(),
  questionRubrics: z.array(QuestionRubric).optional(),
  aiInterviewerDebugMode: AIInterviewerDebugMode.optional(),
  // the start date of applications to consider for AI interviewer
  aiInterviewerStartDate: z.string().optional(),
  isConvertToParquetEnabled: z.boolean().default(false),
  isResumeParsingEnabled: z.boolean().optional(),
  isResumeParsingFilteringDisabled: z.boolean().default(false),
  passthroughRequests: z.array(MergeIntegrationPassthroughRequest).optional(),
  isIncrementalSyncEnabled: z.boolean().default(false),
  isResumeExtraFeaturesEnabled: z.boolean().default(false),
  resumeParsingCacheBusterVersion: z
    .string()
    .default(ResumeParsingCacheBusterDefaultVersion),
  snowflakeIntegrationEnabled: z.boolean().default(false).optional(),
}).merge(ResumeQualityScoreConfig);

export type MergeIntegration = z.infer<typeof MergeIntegration>;
